/* Open the existing Shopify chat only on request. No messages or customer data sent. */
(() => {
  'use strict';
  const selector = '[data-bexco-chat-open], .bexco-fitment-help a[href^="mailto:bexco.automotive@gmail.com"]';
  const fallbackURL = 'mailto:bexco.automotive@gmail.com';
  let pending = null;
  const feedback = new WeakMap();

  function enhance(root = document) {
    root.querySelectorAll(selector).forEach(link => {
      if (link.hasAttribute('data-bexco-chat-fallback')) return;
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
    });
  }

  function statusFor(trigger) {
    let status = feedback.get(trigger);
    if (!status?.isConnected) {
      status = document.createElement('span');
      status.className = 'bexco-chat-feedback';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      trigger.after(status);
      feedback.set(trigger, status);
    }
    return status;
  }

  function stop(cancelOpening = false) {
    if (!pending) return;
    clearTimeout(pending.timer);
    if (cancelOpening && pending.requested && pending.chat && !pending.chat.hasAttribute('shell-ready')) {
      try { if (typeof pending.chat.close === 'function') pending.chat.close(); } catch (_) { /* Keep fallback usable. */ }
    }
    pending.trigger.removeAttribute('aria-busy');
    pending = null;
  }

  function unavailable(request) {
    if (pending !== request) return;
    stop(true);
    request.status.textContent = 'Chat isn’t loading right now. Try again, or ';
    const email = document.createElement('a');
    email.href = fallbackURL;
    email.textContent = 'email Bexco';
    // Deliberately outside the enhancement selector, including in PDP help copy.
    email.setAttribute('data-bexco-chat-fallback', '');
    request.status.append(email, '.');
  }

  function openChat(trigger) {
    if (pending?.trigger === trigger) return;
    if (pending) pending.status.textContent = '';
    stop(true);
    const status = statusFor(trigger);
    status.textContent = 'Opening chat…';
    trigger.setAttribute('aria-busy', 'true');
    const request = { trigger, status, deadline: Date.now() + 5000, timer: null, requested: false };
    pending = request;

    function attempt() {
      if (pending !== request) return;
      if (!trigger.isConnected || document.visibilityState === 'hidden') { status.textContent = ''; stop(true); return; }
      const chat = document.querySelector('shopify-chat');
      try {
        // Observed interface in Shopify's current storefront/web-components/chat.js.
        // Feature detection and a bounded fallback protect against vendor changes.
        if (chat && typeof chat.show === 'function') {
          if (!request.requested && chat.open !== true) { request.requested = true; request.chat = chat; chat.show(); }
          if (chat.open === true && chat.hasAttribute('shell-ready')) { status.textContent = ''; stop(); return; }
        }
      } catch (_) { unavailable(request); return; }
      if (Date.now() >= request.deadline) { unavailable(request); return; }
      request.timer = setTimeout(attempt, 100);
    }
    attempt();
  }

  function triggerFor(event) {
    if (!(event.target instanceof Element)) return null;
    const link = event.target.closest(selector);
    return link && !link.hasAttribute('data-bexco-chat-fallback') ? link : null;
  }

  document.addEventListener('click', event => {
    const trigger = triggerFor(event);
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    openChat(trigger);
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && pending) { pending.status.textContent = ''; stop(true); }
    if (event.key !== ' ' || event.repeat || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    const trigger = triggerFor(event);
    if (trigger) { event.preventDefault(); openChat(trigger); }
  });
  window.addEventListener('pagehide', () => { if (pending) pending.status.textContent = ''; stop(true); });
  enhance();
  // Native Shopify variant updates can replace PDP help markup.
  if (typeof MutationObserver === 'function') {
    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1))) enhance();
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
